type ApiResponse<T> = {
    data: T | null;
    error: string | null;
};

const DEFAULT_ERROR = "Something went wrong. Please try again.";

const handleRequest = async <T>(fn: () => Promise<any>): Promise<ApiResponse<T>> => {
    try {
        const res = await fn();

        // Handle empty / invalid response
        if (!res) {
            return { data: null, error: "Empty response from server" };
        }

        // If your API wraps response like { data }
        if (res?.data !== undefined) {
            return { data: res.data, error: null };
        }

        // fallback (in case API returns raw)
        return { data: res, error: null };

    } catch (err: any) {
        console.error("API Error:", err);

        return {
            data: null,
            error:
                err?.data?.message ||
                err?.message ||
                DEFAULT_ERROR,
        };
    }
};

export const LocationService = {
    // Geocode API
    async geocode(address: string): Promise<ApiResponse<any>> {
        if (!address || !address.trim()) {
            return { data: null, error: "Invalid address" };
        }

        return handleRequest(() =>
            $apiServer("/maps/geocode", {
                method: "POST",
                body: { address: address.trim() },
            })
        );
    },

    // Reverse Geocode API
    async reverseGeocode(coordinates: { lat: number; lng: number }): Promise<ApiResponse<any>> {
        const { lat, lng } = coordinates || {};

        if (
            lat === undefined ||
            lng === undefined ||
            isNaN(lat) ||
            isNaN(lng)
        ) {
            return { data: null, error: "Invalid coordinates" };
        }

        return handleRequest(() =>
            $apiServer("/maps/geocode/reverse", {
                method: "POST",
                body: { lat, lng },
            })
        );
    },

    // Cities Search API
    async searchCities(query: string): Promise<ApiResponse<any>> {
        if (!query || !query.trim()) {
            return { data: [], error: null };
        }

        return handleRequest(() =>
            $apiServer("/cities/search", {
                method: "GET",
                query: {
                    name: query.trim(),
                },
            })
        );
    },
};